import React, { useEffect, useRef, useState } from 'react';
import GameRecap from './GameRecap';
import { nflMarkets, nflPlayerMarkets } from '../odds';
import api from '../api';
import { fmtDateTime } from '../utils/time';
import { vibrate } from '../haptics';
import { isDemo, demoNflStats } from '../dev/demo';
import EmptyState from './EmptyState';
import { fmtOdds, fmtMoneyline } from '../utils/oddsFmt';

// NFL: this week's games (Thursday → Monday), same card as the other sports.
// Open a game: moneyline, spread, total, 1st half, game props, touchdown scorers, player yards.
const fmtLine = (x) => (x > 0 ? `+${x}` : `${x}`);
const kickoff = (iso, lang) => fmtDateTime(iso, lang);   // device time zone + 12 h / 24 h
const qLabel = (p, lang) => (p <= 4 ? `Q${p}` : lang === 'fr' ? 'Prol.' : 'OT');

// the live-stats bars: left = away, right = home, in each team's colour
function StatBar({ label, away, home, lowerIsBetter, fmt }) {
  const a = Number(away) || 0, h = Number(home) || 0;
  const aWin = lowerIsBetter ? a < h : a > h, hWin = lowerIsBetter ? h < a : h > a;
  const cls = (win, other) => (win ? 'winning' : a !== h ? 'losing' : 'tied') + (other ? '' : '');
  return (
    <div className="stat-bar-row">
      <span className="stat-val left">{fmt ? fmt(away) : a}</span>
      <div className="stat-bar-container">
        <div className="stat-bar-label">{label}</div>
        <div className="stat-bar">
          <div className={`stat-bar-fill away ${cls(aWin)}`} style={{ width: `${(a / Math.max(a + h, 1)) * 100}%` }}></div>
          <div className={`stat-bar-fill home ${cls(hWin)}`} style={{ width: `${(h / Math.max(a + h, 1)) * 100}%` }}></div>
        </div>
      </div>
      <span className="stat-val right">{fmt ? fmt(home) : h}</span>
    </div>
  );
}
function StatText({ label, away, home }) {
  return (
    <div className="stat-bar-row">
      <span className="stat-val left">{away}</span>
      <div className="stat-bar-container"><div className="stat-bar-label">{label}</div></div>
      <span className="stat-val right">{home}</span>
    </div>
  );
}

export default function NflGames({ games, lang = 'en', expandedId, onToggle, onBet, props, empty, favorite = '' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [stats, setStats] = useState({});    // gameId -> live / final panel
  const [td, setTd] = useState({});          // gameId -> { team, logo } while the touchdown animation plays
  const lastScore = useRef({});
  const expandedGame = (games || []).find((g) => g.id === expandedId);
  const expandedState = expandedGame?.status;

  // the opened game's panel: every 20 s while live, once when final
  useEffect(() => {
    if (!expandedGame || !['LIVE', 'OFF'].includes(expandedState)) return undefined;
    const load = () => {
      const demo = demoNflStats(expandedGame);
      if (demo) { setStats((s) => ({ ...s, [expandedGame.id]: demo })); return; }
      api.fetchNFLGameStats(expandedGame.id).then((x) => x && setStats((s) => ({ ...s, [expandedGame.id]: x }))).catch(() => {});
    };
    load();
    if (expandedState !== 'LIVE') return undefined;
    const id = setInterval(load, 20 * 1000);
    return () => clearInterval(id);
  }, [expandedId, expandedState]);   // eslint-disable-line

  // touchdown: a team's score jumps by 6+ during a live game → animation + vibration
  const celebrate = (g, side) => {
    const logo = side === 'home' ? g.home_logo : g.away_logo;
    setTd((p) => ({ ...p, [g.id]: { team: side === 'home' ? g.home_abbrev : g.away_abbrev, logo } }));
    vibrate('goal');
    setTimeout(() => setTd((p) => { const x = { ...p }; delete x[g.id]; return x; }), 5000);
  };
  useEffect(() => {
    (games || []).forEach((g) => {
      const prev = lastScore.current[g.id];
      if (prev && g.status === 'LIVE') {
        if (g.home_score - prev.home >= 6) celebrate(g, 'home');
        else if (g.away_score - prev.away >= 6) celebrate(g, 'away');
      }
      lastScore.current[g.id] = { home: g.home_score, away: g.away_score };
    });
  }, [games]);   // eslint-disable-line
  // demo: the live game scores a touchdown a few seconds in
  useEffect(() => {
    if (!isDemo()) return undefined;
    const g = (games || []).find((x) => x.status === 'LIVE');
    if (!g) return undefined;
    const id = setTimeout(() => celebrate(g, 'away'), 4000);
    return () => clearTimeout(id);
  }, [!!(games || []).find((x) => x.status === 'LIVE')]);   // eslint-disable-line

  if (!games) return <EmptyState icon="loading" title={t.loading} />;
  if (!games.length) return empty || <EmptyState icon="calendar" title={t.none} />;
  return (
    <div className="games-grid">
      {games.map((game) => {
        const live = game.status === 'LIVE', final = game.status === 'OFF';
        const bettable = game.status === 'FUT' && !!game.model;
        const canOpen = bettable || live || final;
        const expanded = canOpen && expandedId === game.id;
        const mk = expanded && bettable ? nflMarkets(game) : null;
        const panel = stats[game.id];
        const colours = { '--away-c': game.away_color || 'var(--text-muted)', '--home-c': game.home_color || 'var(--accent)' };
        const homeLine = mk ? -mk.spread.line : null, awayLine = mk ? mk.spread.line : null;
        const explain = (teamLine) => (teamLine < 0
          ? (lang === 'fr' ? `Gagne par ${Math.ceil(-teamLine)}+` : `Wins by ${Math.ceil(-teamLine)}+`)
          : (lang === 'fr' ? `Ne perdra pas par ${Math.ceil(teamLine)}+` : `Won't lose by ${Math.ceil(teamLine)}+`));
        const pr = props?.[game.id];
        const players = pr ? [...pr.away, ...pr.home].map((p) => ({ ...p, mk: nflPlayerMarkets(game, p) })).filter((p) => p.mk) : [];
        const scorers = players.filter((p) => p.mk.anytime).sort((a, b) => (b.rates.td || 0) - (a.rates.td || 0)).slice(0, 18);
        const yardRows = (kind) => players.filter((p) => p.mk[kind]).sort((a, b) => b.rates[kind] - a.rates[kind]).slice(0, 10);
        const logo = (p) => (p.team === game.home_abbrev ? game.home_logo : game.away_logo);
        return (
          <div key={game.id} className={`game-card${expanded ? ' expanded' : ''}${live ? ' live-game' : ''}${final ? ' finished-game' : ''}${favorite && (game.home_abbrev === favorite || game.away_abbrev === favorite) ? ' fav-game' : ''}`}>
            <div className="game-header" onClick={() => canOpen && onToggle(game.id)}>
              <div className="nhl-scoreboard nhl-scoreboard--nba">
                <div className="nhl-main-row">
                  <div className="nhl-team away">
                    <img src={game.away_logo} alt={game.away_abbrev} className="team-logo nba-logo" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="nhl-name">{game.away_team}</span>
                  </div>
                  <div className="nhl-score-row">
                    {(live || final) ? (
                      <>
                        <span className={`nhl-score away${game.away_score > game.home_score ? ' winning' : game.away_score < game.home_score ? ' losing' : ''}`}>{game.away_score}</span>
                        <div className={`nhl-period${live ? ' live' : ''}${final ? ' final' : ''}`}>
                          {live && <span className="live-dot"></span>}
                          <span>{live ? `${qLabel(game.period, lang)} ${game.game_clock}` : 'FINAL'}</span>
                        </div>
                        <span className={`nhl-score home${game.home_score > game.away_score ? ' winning' : game.home_score < game.away_score ? ' losing' : ''}`}>{game.home_score}</span>
                      </>
                    ) : (
                      <div className="nhl-upcoming">
                        <span className="nhl-time-main">{kickoff(game.game_time, lang)}</span>
                        <span className="nhl-venue">{game.venue}</span>
                      </div>
                    )}
                  </div>
                  <div className="nhl-team home">
                    <img src={game.home_logo} alt={game.home_abbrev} className="team-logo nba-logo" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="nhl-name">{game.home_team}</span>
                  </div>
                </div>
              </div>
              {bettable && (
                <div className="quick-odds">
                  <button className="odd-btn" onClick={(e) => { e.stopPropagation(); onBet(game, 'winner', 'away', {}); }}>
                    <span>{game.away_abbrev}</span><span className="odd">{fmtMoneyline(game.away_odds)}</span>
                  </button>
                  <button className="odd-btn" onClick={(e) => { e.stopPropagation(); onBet(game, 'winner', 'home', {}); }}>
                    <span>{game.home_abbrev}</span><span className="odd">{fmtMoneyline(game.home_odds)}</span>
                  </button>
                </div>
              )}
            </div>

            {td[game.id] && (() => {
              const a = td[game.id];
              const copies = Array(15).fill('TOUCHDOWN');
              return (
                <div className="hr-overlay">
                  <div className="hr-flash" />
                  <div className="hr-rows-container">
                    {[-0, -15, -30, -45].map((delay, ri) => (
                      <div key={ri} className="hr-row">
                        <div className="hr-row-inner" style={{ animationDelay: `${delay}s` }}>
                          {[...copies, ...copies].map((w, i) => <span key={i}>{w}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hr-overlay-content">
                    <div className="hr-overlay-main">
                      {a.logo && <img src={a.logo} alt={a.team} className="hr-team-logo" style={{ width: 100, height: 100 }} onError={(e) => { e.target.style.display = 'none'; }} />}
                      <span className="hr-overlay-text" style={{ color: '#2d2d2d', textShadow: '0 4px 16px rgba(0,0,0,0.3), 0 2px 0 rgba(0,0,0,0.15)' }}>TOUCHDOWN!</span>
                      {a.logo && <img src={a.logo} alt={a.team} className="hr-team-logo" style={{ width: 100, height: 100 }} onError={(e) => { e.target.style.display = 'none'; }} />}
                    </div>
                  </div>
                </div>
              );
            })()}

            {final && <GameRecap sport="nfl" game={game} final onClick={() => onToggle(game.id)} />}
            {expanded && live && <GameRecap sport="nfl" game={game} />}
            {expanded && (live || final) && (
              <div className="live-stats-panel" style={colours}>
                <h4>{t.statistics}</h4>
                {!panel ? (
                  <div className="stats-loading">{t.loadingStats}</div>
                ) : (<>
                  <div className="stats-grid">
                    <StatBar label={t.totalYards} away={panel.away.stats.totalYards} home={panel.home.stats.totalYards} />
                    <StatBar label={t.passing} away={panel.away.stats.passingYards} home={panel.home.stats.passingYards} />
                    <StatBar label={t.rushing} away={panel.away.stats.rushingYards} home={panel.home.stats.rushingYards} />
                    <StatBar label={t.firstDowns} away={panel.away.stats.firstDowns} home={panel.home.stats.firstDowns} />
                    <StatBar label={t.turnovers} away={panel.away.stats.turnovers} home={panel.home.stats.turnovers} lowerIsBetter />
                    <StatBar label={t.sacks} away={panel.away.stats.sacks} home={panel.home.stats.sacks} lowerIsBetter />
                    <StatBar label={t.possession} away={panel.away.stats.possessionSec} home={panel.home.stats.possessionSec}
                      fmt={(v) => `${Math.floor((Number(v) || 0) / 60)}:${String((Number(v) || 0) % 60).padStart(2, '0')}`} />
                    <StatText label={t.thirdDown} away={panel.away.stats.thirdDown} home={panel.home.stats.thirdDown} />
                    <StatText label={t.penalties} away={panel.away.stats.penalties} home={panel.home.stats.penalties} />
                  </div>
                  {panel.scoringPlays?.length > 0 && (
                    <div className="pbp-feed">
                      <h5>{t.scoringPlays}</h5>
                      <div className="pbp-list">
                        {panel.scoringPlays.map((p, i) => (
                          <div key={i} className={`pbp-play ${p.type}`}>
                            <span className="pbp-time">{qLabel(p.period, lang)} {p.clock}</span>
                            <span className="pbp-team">{p.team}</span>
                            <span className="pbp-desc">{p.kind ? <b>{p.kind} · </b> : null}{p.text} <small>({p.awayScore}-{p.homeScore})</small></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>)}
              </div>
            )}

            {expanded && mk && (
              <div className="markets">
                <div className="market">
                  <h5>{t.moneyline}</h5>
                  <p className="market-desc">{t.moneylineDesc}</p>
                  <div className="market-row">
                    <button onClick={() => onBet(game, 'winner', 'away', {})}>{game.away_team} <span>{fmtMoneyline(game.away_odds)}</span></button>
                    <button onClick={() => onBet(game, 'winner', 'home', {})}>{game.home_team} <span>{fmtMoneyline(game.home_odds)}</span></button>
                  </div>
                </div>

                <div className="market">
                  <h5>{t.spread}</h5>
                  <p className="market-desc">{t.spreadDesc}</p>
                  <div className="market-row puckline-row">
                    <button onClick={() => onBet(game, 'spread', 'away', { team: game.away_abbrev, line: awayLine })} className="puckline-btn">
                      <div className="puckline-team">{game.away_team}</div>
                      <div className="puckline-line">{fmtLine(awayLine)}</div>
                      <div className="puckline-explain">{explain(awayLine)}</div>
                      <span className="puckline-odds">{fmtOdds(mk.spread.away)}</span>
                    </button>
                    <button onClick={() => onBet(game, 'spread', 'home', { team: game.home_abbrev, line: homeLine })} className="puckline-btn">
                      <div className="puckline-team">{game.home_team}</div>
                      <div className="puckline-line">{fmtLine(homeLine)}</div>
                      <div className="puckline-explain">{explain(homeLine)}</div>
                      <span className="puckline-odds">{fmtOdds(mk.spread.home)}</span>
                    </button>
                  </div>
                </div>

                <div className="market">
                  <h5>{t.total} (O/U {mk.total.line})</h5>
                  <p className="market-desc">{t.totalDesc}</p>
                  <div className="market-row">
                    <button onClick={() => onBet(game, 'total', 'over', { type: 'Over', line: mk.total.line })}>Over {mk.total.line} <span>{fmtOdds(mk.total.over)}</span></button>
                    <button onClick={() => onBet(game, 'total', 'under', { type: 'Under', line: mk.total.line })}>Under {mk.total.line} <span>{fmtOdds(mk.total.under)}</span></button>
                  </div>
                </div>

                <div className="market">
                  <h5>{t.half}</h5>
                  <p className="market-desc">{t.halfDesc}</p>
                  <div className="market-row three">
                    <button onClick={() => onBet(game, 'nfl_half_winner', 'away', { pick: game.away_team })}>{game.away_abbrev} <span>{fmtOdds(mk.half.away)}</span></button>
                    <button onClick={() => onBet(game, 'nfl_half_winner', 'tie', { pick: t.tie })}>{t.tie} <span>{fmtOdds(mk.half.tie)}</span></button>
                    <button onClick={() => onBet(game, 'nfl_half_winner', 'home', { pick: game.home_team })}>{game.home_abbrev} <span>{fmtOdds(mk.half.home)}</span></button>
                  </div>
                </div>

                <div className="market">
                  <h5>{t.props}</h5>
                  <p className="market-desc">{t.propsDesc}</p>
                  <div className="market-row three">
                    <button onClick={() => onBet(game, 'nfl_both_20', 'yes', {})}>{t.both20} <span>{fmtOdds(mk.both20.yes)}</span></button>
                    <button onClick={() => onBet(game, 'nfl_both_20', 'no', {})}>{t.notBoth20} <span>{fmtOdds(mk.both20.no)}</span></button>
                    <button onClick={() => onBet(game, 'nfl_overtime', 'yes', {})}>{t.ot} <span>{fmtOdds(mk.overtime)}</span></button>
                  </div>
                </div>

                {!pr ? (
                  <div className="market"><p className="market-desc">{t.loadingPlayers}</p></div>
                ) : (
                  <>
                    {[['nfl_anytime_td', 'anytime', t.anytime, t.anytimeDesc], ['nfl_first_td', 'first', t.first, t.firstDesc]].map(([bt, key, title, desc]) => (
                      <div className="market" key={bt}>
                        <h5>{title}</h5>
                        <p className="market-desc">{desc}</p>
                        <div className="player-grid">
                          {scorers.map((p) => (
                            <button key={p.id} disabled={!p.mk[key]} onClick={() => onBet(game, bt, 'player', { playerId: p.id, playerName: p.name })}>
                              <span className="pg-player"><img className="prop-team-logo" alt="" src={logo(p)} /><span className="pg-name">{p.name}</span></span> <span>{fmtOdds(p.mk[key])}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {[['nfl_pass_yds', 'pass', t.passYds], ['nfl_rush_yds', 'rush', t.rushYds], ['nfl_rec_yds', 'rec', t.recYds]].map(([bt, kind, title]) => {
                      const rows = yardRows(kind);
                      if (!rows.length) return null;
                      return (
                        <div className="market" key={bt}>
                          <h5>{title}</h5>
                          <p className="market-desc">{t.yardsDesc}</p>
                          <div className="player-prop-list">
                            {rows.map((p) => {
                              const y = p.mk[kind];
                              return (
                                <div key={p.id} className="player-prop-row">
                                  <span className="player-prop-name"><img className="prop-team-logo" alt="" src={logo(p)} /><span>{p.name}</span></span>
                                  <div className="prop-btns" style={{ '--n': 2 }}>
                                    <button onClick={() => onBet(game, bt, 'over', { playerId: p.id, playerName: p.name, line: y.line, type: 'Over' })}>O{y.line} <span>{fmtOdds(y.over)}</span></button>
                                    <button onClick={() => onBet(game, bt, 'under', { playerId: p.id, playerName: p.name, line: y.line, type: 'Under' })}>U{y.line} <span>{fmtOdds(y.under)}</span></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const TR = {
  en: {
    loading: 'Loading games…', none: 'No NFL games this week', loadingPlayers: 'Loading players…',
    moneyline: 'Moneyline', moneylineDesc: 'Pick the winner (a tie refunds)',
    spread: 'Point Spread', spreadDesc: 'Pick the winner with the point spread',
    total: 'Total Points', totalDesc: 'Combined points of both teams',
    half: '1st Half Winner', halfDesc: 'Who leads at halftime', tie: 'Tie',
    props: 'Game Props', propsDesc: 'Special bets on the game', both20: 'Both 20+', notBoth20: 'Not both 20+', ot: 'Overtime',
    anytime: 'Anytime Touchdown', anytimeDesc: 'Scores a rushing or receiving touchdown',
    first: 'First Touchdown', firstDesc: 'Scores the first touchdown of the game',
    passYds: 'Passing Yards', rushYds: 'Rushing Yards', recYds: 'Receiving Yards', yardsDesc: 'Over or under the player’s line',
    statistics: 'Game stats', loadingStats: 'Loading stats…', totalYards: 'Total yards', passing: 'Passing yards', rushing: 'Rushing yards',
    firstDowns: '1st downs', turnovers: 'Turnovers', sacks: 'Sacks taken', possession: 'Possession', thirdDown: '3rd down', penalties: 'Penalties',
    scoringPlays: 'Scoring plays',
  },
  fr: {
    loading: 'Chargement des matchs…', none: 'Aucun match NFL cette semaine', loadingPlayers: 'Chargement des joueurs…',
    moneyline: 'Gagnant', moneylineDesc: 'Choisis le gagnant (match nul = remboursé)',
    spread: 'Écart de points', spreadDesc: 'Choisis le gagnant avec l’écart',
    total: 'Total de points', totalDesc: 'Points des deux équipes',
    half: 'Gagnant de la 1re demie', halfDesc: 'Qui mène à la mi-temps', tie: 'Égalité',
    props: 'Paris spéciaux', propsDesc: 'Paris spéciaux sur le match', both20: 'Les deux 20+', notBoth20: 'Pas les deux 20+', ot: 'Prolongation',
    anytime: 'Touché (n’importe quand)', anytimeDesc: 'Marque un touché au sol ou par la passe',
    first: 'Premier touché', firstDesc: 'Marque le premier touché du match',
    passYds: 'Verges par la passe', rushYds: 'Verges au sol', recYds: 'Verges sur réception', yardsDesc: 'Plus ou moins que la ligne du joueur',
    statistics: 'Statistiques', loadingStats: 'Chargement des stats…', totalYards: 'Verges totales', passing: 'Verges par la passe', rushing: 'Verges au sol',
    firstDowns: '1ers essais', turnovers: 'Revirements', sacks: 'Sacs subis', possession: 'Possession', thirdDown: '3e essai', penalties: 'Pénalités',
    scoringPlays: 'Jeux marqués',
  },
};
